/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import type { WbaxAutomation, WbaxOscProps } from "@chi/wbax";
import { NonZero } from "@/synth/synth.tsx";

export const BiquadTypes = [
  "allpass",
  "bandpass",
  "highpass",
  "highshelf",
  "lowpass",
  "lowshelf",
  "notch",
  "peaking",
] as const;

export const OscTypes = ["sine", "square", "sawtooth", "triangle"] as const;

export const HatRatios = [1, 1.342, 1.2312, 1.6532, 1.9523, 2.1523];

export interface SharpDecayEnvProps {
  volume: number;
  decay: number;
  floor?: number;
  tail?: number;
}
export function SharpDecayEnv(props: SharpDecayEnvProps): WbaxAutomation {
  const { volume, decay, floor = 0.001, tail = 0 } = props;
  return [
    { set: volume, at: 0 },
    { exp: NonZero(floor * volume), at: decay },
    ...(tail > 0 ? [{ linear: 0, at: decay + tail }] : []),
  ];
}

export interface PercDecayEnvProps {
  volume: number;
  decay: number;
  start?: number;
  peak?: number;
  mid?: number;
  attackRatio?: number;
  midRatio?: number;
}
export function PercDecayEnv(props: PercDecayEnvProps): WbaxAutomation {
  const {
    volume,
    decay,
    start = 0.00001,
    peak = 1,
    mid = 0.3,
    attackRatio = 0.067,
    midRatio = 0.1,
  } = props;
  const v = NonZero(volume);
  return [
    { set: start * v, at: 0 },
    { exp: NonZero(peak * v), at: attackRatio * decay },
    { exp: NonZero(mid * v), at: midRatio * decay },
    { exp: NonZero(start * v), at: decay },
  ];
}

export interface KickEnvProps {
  start?: number;
  volume: number;
  attack: number;
  decay: number;
  tail: number;
}
export function KickEnv(props: KickEnvProps): WbaxAutomation {
  const v = NonZero(props.volume);
  return [
    { set: props.start ?? 1, at: 0 },
    { linear: v, at: props.attack },
    { exp: 0.01 * v, at: props.decay },
    { linear: 0, at: props.decay + props.tail },
  ];
}

export interface PulseEnvProps {
  volume: number;
  width: number;
  decay: number;
  count: number;
}
export function PulseEnv(props: PulseEnvProps): WbaxAutomation {
  const { volume, width, decay, count } = props;
  const env: WbaxAutomation = [];
  for (let i = 0; i < count; i++) {
    env.push({ set: volume, at: i * width });
    env.push(i < count - 1 ? { exp: 0.1, at: (i + 1) * width } : { exp: 0.001, at: decay });
  }
  return env;
}

export function FilterSweep(frequency: number, ramp: number, duration: number): WbaxAutomation {
  return [{ set: frequency, at: 0 }, { linear: ramp, at: duration }];
}

export function FadeEnv(level: number, up: number, down: number, duration: number): WbaxAutomation {
  const rampUp = Math.min(up, duration / 2);
  const rampDown = Math.min(down, duration / 2);
  return [
    { set: 0, at: 0 },
    { linear: level, at: rampUp },
    { set: level, at: duration - rampDown },
    { linear: 0, at: duration },
  ];
}

export const RampTypes = ["linear", "exp"] as const;
export type RampType = (typeof RampTypes)[number];

export const Ramp = (type: RampType, value: number, at: number) =>
  type === "exp" ? { exp: NonZero(value), at } : { linear: value, at };

export interface MultiOscProps {
  type?: WbaxOscProps["type"];
  frequency: number;
  detune?: number;
  ratios: number[];
  stop?: number;
  onEnded?: () => void;
}
export const MultiOsc = (props: MultiOscProps) => (
  <gain gain={1 / Math.max(props.ratios.length, 1)}>
    {props.ratios.map((ratio, i) => (
      <osc
        key={i}
        type={props.type}
        frequency={props.frequency * ratio}
        detune={props.detune}
        stop={props.stop}
        onEnded={i === 0 ? props.onEnded : undefined}
      />
    ))}
  </gain>
);
